import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Edit, Check, X, TestTube, Plus, FileDown } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { EmailIntakeOverview } from "./email-intake-overview";

interface EmailAccount {
  id: string;
  name: string;
  email: string;
  displayName?: string;
  host: string;
  port: number;
  username: string;
  isPrimary: boolean;
  isActive: boolean;
  type: 'imap' | 'smtp';
  sendingScope: 'shared' | 'personal';
  ownerUserId: number | null;
}

interface TeamMemberOption {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: string;
}

const NO_OWNER = 'none';
type SendingScopeChoice = '' | 'shared' | 'personal';

interface PrimaryReplacementPrompt {
  currentPrimary: { id: string; email: string; name: string };
  payload: any;
}

/** Server refuses to demote the current primary mailbox without explicit confirmation. */
function readPrimaryReplacementConflict(error: any): PrimaryReplacementPrompt['currentPrimary'] | null {
  const body = error?.body;
  return error?.status === 409 && body?.code === 'PRIMARY_REPLACEMENT_CONFIRMATION_REQUIRED' && body.currentPrimary
    ? body.currentPrimary
    : null;
}

function memberLabel(member: TeamMemberOption): string {
  const name = [member.firstName, member.lastName].filter(Boolean).join(' ').trim();
  return name ? `${name} (${member.email})` : member.email;
}

interface EmailSetupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EmailSetupModal({ open, onOpenChange }: EmailSetupModalProps) {
  const { user } = useAuth();
  const [editingAccount, setEditingAccount] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [testingAccount, setTestingAccount] = useState<string | null>(null);
  const [primaryPrompt, setPrimaryPrompt] = useState<PrimaryReplacementPrompt | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form state for new/editing accounts
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    displayName: '',
    host: '',
    port: 143,
    username: '',
    password: '',
    type: 'imap' as 'imap' | 'smtp',
    isPrimary: false,
    // No default: the admin must state who may send from a new address.
    sendingScope: '' as SendingScopeChoice,
    ownerUserId: null as number | null,
  });

  const { data: emailAccounts, isLoading } = useQuery<EmailAccount[]>({
    queryKey: ['/api/email-accounts'],
    enabled: open
  });

  // Owner candidates for personal accounts: active internal users.
  const { data: teamMembers } = useQuery<TeamMemberOption[]>({
    queryKey: ['/api/users/team-members'],
    enabled: open
  });
  const ownerName = (ownerUserId: number | null) => {
    if (ownerUserId == null) return null;
    const member = teamMembers?.find(candidate => candidate.id === ownerUserId);
    return member ? memberLabel(member) : `user #${ownerUserId}`;
  };

  const createAccountMutation = useMutation({
    mutationFn: async (accountData: any) => {
      const response = await apiRequest('POST', '/api/email-accounts', accountData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-accounts'] });
      setShowAddForm(false);
      resetForm();
      toast({ title: "Email account created successfully" });
    },
    onError: (error: any, accountData: any) => {
      const currentPrimary = readPrimaryReplacementConflict(error);
      if (currentPrimary) {
        setPrimaryPrompt({ currentPrimary, payload: accountData });
        return;
      }
      toast({ title: "Error creating email account", description: error.message, variant: "destructive" });
    }
  });

  const updateAccountMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await apiRequest('PATCH', `/api/email-accounts/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-accounts'] });
      setEditingAccount(null);
      toast({ title: "Email account updated successfully" });
    },
    onError: (error: any, variables) => {
      const currentPrimary = readPrimaryReplacementConflict(error);
      if (currentPrimary) {
        setPrimaryPrompt({ currentPrimary, payload: { id: variables.id, data: variables.data } });
        return;
      }
      toast({ title: "Error updating email account", description: error.message, variant: "destructive" });
    }
  });

  const confirmPrimaryReplacement = () => {
    if (!primaryPrompt) return;
    const { payload } = primaryPrompt;
    setPrimaryPrompt(null);
    if (payload.id && payload.data) {
      updateAccountMutation.mutate({ id: payload.id, data: { ...payload.data, confirmReplacePrimary: true } });
    } else {
      createAccountMutation.mutate({ ...payload, confirmReplacePrimary: true });
    }
  };

  const deleteAccountMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('DELETE', `/api/email-accounts/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-accounts'] });
      toast({ title: "Email account deleted successfully" });
    }
  });

  const testConnectionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('POST', `/api/email-accounts/${id}/test`);
      return response.json();
    },
    onSuccess: (data, accountId) => {
      setTestingAccount(null);
      if (data.success) {
        toast({ title: "Connection successful", description: data.message });
      } else {
        toast({ title: "Connection failed", description: data.message, variant: "destructive" });
      }
    },
    onError: () => {
      setTestingAccount(null);
      toast({ title: "Test failed", description: "Unable to test connection", variant: "destructive" });
    }
  });

  const downloadEmailReport = async () => {
    try {
      const response = await apiRequest('GET', '/api/reports/email-system');
      if (!response.ok) throw new Error('Failed to generate report');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `email-system-report-${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({ title: "Report downloaded", description: "Email system report has been downloaded successfully" });
    } catch (error) {
      console.error('Error downloading report:', error);
      toast({ title: "Download failed", description: "Failed to download email system report", variant: "destructive" });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      displayName: '',
      host: '',
      port: 143,
      username: '',
      password: '',
      type: 'imap',
      isPrimary: false,
      sendingScope: '',
      ownerUserId: null,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.sendingScope) {
      toast({ title: "Choose who may send from this address", variant: "destructive" });
      return;
    }
    // A shared account never carries an owner; the server enforces the same.
    const payload = {
      ...formData,
      ownerUserId: formData.sendingScope === 'personal' ? formData.ownerUserId : null,
    };
    if (formData.sendingScope === 'personal' && !payload.ownerUserId) {
      toast({ title: "Choose the owner of this personal address", variant: "destructive" });
      return;
    }
    if (editingAccount) {
      // An empty password means "keep the stored one"; PATCH is partial.
      const { password, ...rest } = payload;
      updateAccountMutation.mutate({ id: editingAccount, data: password ? payload : rest });
    } else {
      createAccountMutation.mutate(payload);
    }
  };

  // Server/port preset only. Credentials are never shipped in the client
  // bundle; the admin types the mailbox password. The preset deliberately
  // does not decide primary status or sending scope: an employee mailbox
  // must never replace the team inbox as primary by accident.
  const populateHostEuropeDefaults = () => {
    setFormData(current => ({
      ...current,
      host: 'wp1041646.mail.server-he.de',
      port: 143,
      type: 'imap',
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] navy-card border-gray-600 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-gray-100">E-Mail Setup</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {open && user?.role === 'admin' && (
            <EmailIntakeOverview accounts={emailAccounts ?? []} />
          )}
          {/* Existing Email Accounts */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-100">Email Accounts</h3>
              <div className="flex gap-2">
                <Button
                  onClick={downloadEmailReport}
                  size="sm"
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Download Report
                </Button>
                <Button
                  onClick={() => setShowAddForm(true)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Account
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div className="text-gray-400">Loading email accounts...</div>
            ) : emailAccounts && emailAccounts.length > 0 ? (
              <div className="space-y-3">
                {emailAccounts.map((account) => (
                  <Card key={account.id} className="navy-card border-gray-600">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <CardTitle className="text-gray-100 text-base">{account.name}</CardTitle>
                          <div className="flex space-x-2">
                            {account.isPrimary && (
                              <Badge variant="outline" className="border-blue-500 text-blue-400">
                                Primary
                              </Badge>
                            )}
                            <Badge variant="outline" className="border-gray-500 text-gray-400">
                              {account.type.toUpperCase()}
                            </Badge>
                            {account.sendingScope === 'personal' ? (
                              <Badge variant="outline" className="border-violet-500 text-violet-300" data-testid={`badge-scope-${account.id}`}>
                                Personal · {ownerName(account.ownerUserId) ?? 'no owner'}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="border-teal-500 text-teal-300" data-testid={`badge-scope-${account.id}`}>
                                Shared team account
                              </Badge>
                            )}
                            <Badge 
                              variant="outline" 
                              className={account.isActive ? "border-green-500 text-green-400" : "border-red-500 text-red-400"}
                            >
                              {account.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setTestingAccount(account.id);
                              testConnectionMutation.mutate(account.id);
                            }}
                            disabled={testingAccount === account.id}
                            className="text-gray-400 hover:text-white"
                          >
                            <TestTube className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingAccount(account.id);
                              setFormData({
                                name: account.name,
                                email: account.email,
                                displayName: account.displayName || '',
                                host: account.host,
                                port: account.port,
                                username: account.username,
                                password: '', // Keep password empty for security
                                type: account.type,
                                isPrimary: account.isPrimary,
                                sendingScope: account.sendingScope ?? '',
                                ownerUserId: account.ownerUserId ?? null,
                              });
                            }}
                            className="text-gray-400 hover:text-white"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteAccountMutation.mutate(account.id)}
                            className="text-gray-400 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">Email:</span>
                          <span className="text-gray-300 ml-2">{account.email}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Host:</span>
                          <span className="text-gray-300 ml-2">{account.host}:{account.port}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="navy-card border-gray-600">
                <CardContent className="pt-6">
                  <div className="text-center text-gray-400">
                    <p>No email accounts configured</p>
                    <Button
                      onClick={() => setShowAddForm(true)}
                      variant="outline"
                      className="mt-4 border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      Add your first email account
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Add/Edit Account Form */}
          {(showAddForm || editingAccount) && (
            <Card className="navy-card border-gray-600">
              <CardHeader>
                <CardTitle className="text-gray-100">
                  {editingAccount ? "Edit Email Account" : "Add New Email Account"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-gray-300">Account Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-gray-800 border-gray-600 text-gray-300"
                        placeholder="e.g., Primary E-mail"
                        required
                        data-testid="input-account-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-300">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="bg-gray-800 border-gray-600 text-gray-300"
                        placeholder="user@domain.com"
                        required
                        data-testid="input-email-address"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="displayName" className="text-gray-300">Display Name (Optional)</Label>
                    <Input
                      id="displayName"
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-gray-300"
                      placeholder="e.g., John Doe or Company Name"
                      data-testid="input-display-name"
                    />
                    <p className="text-xs text-gray-500 mt-1">This name will appear as the sender when sending emails</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="host" className="text-gray-300">Host Server</Label>
                      <Input
                        id="host"
                        value={formData.host}
                        onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                        className="bg-gray-800 border-gray-600 text-gray-300"
                        placeholder="mail.domain.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="port" className="text-gray-300">Port</Label>
                      <Input
                        id="port"
                        type="number"
                        value={formData.port}
                        onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                        className="bg-gray-800 border-gray-600 text-gray-300"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="username" className="text-gray-300">Username</Label>
                      <Input
                        id="username"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        className="bg-gray-800 border-gray-600 text-gray-300"
                        placeholder="Usually email or account name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="password" className="text-gray-300">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="bg-gray-800 border-gray-600 text-gray-300"
                        placeholder={editingAccount ? "Leave empty to keep the stored password" : undefined}
                        required={!editingAccount}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">Who may send from this address</Label>
                      <Select
                        value={formData.sendingScope || undefined}
                        onValueChange={(value: 'shared' | 'personal') => setFormData({
                          ...formData,
                          sendingScope: value,
                          ownerUserId: value === 'personal' ? formData.ownerUserId : null,
                        })}
                      >
                        <SelectTrigger className="bg-gray-800 border-gray-600 text-gray-300" data-testid="select-sending-scope">
                          <SelectValue placeholder="— choose —" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600">
                          <SelectItem value="shared" className="text-gray-200">Shared team account (every team member)</SelectItem>
                          <SelectItem value="personal" className="text-gray-200">Personal address (owner only)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="mt-1 text-xs text-gray-500">
                        Personal addresses are never delegated: no colleague or admin can send in the owner's name.
                      </p>
                    </div>
                    {formData.sendingScope === 'personal' && (
                      <div>
                        <Label className="text-gray-300">Owner</Label>
                        <Select
                          value={formData.ownerUserId == null ? NO_OWNER : String(formData.ownerUserId)}
                          onValueChange={(value) => setFormData({
                            ...formData,
                            ownerUserId: value === NO_OWNER ? null : Number(value),
                          })}
                        >
                          <SelectTrigger className="bg-gray-800 border-gray-600 text-gray-300" data-testid="select-owner-user">
                            <SelectValue placeholder="Choose the employee" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600">
                            <SelectItem value={NO_OWNER} className="text-gray-400">— choose —</SelectItem>
                            {teamMembers?.map(member => (
                              <SelectItem key={member.id} value={String(member.id)} className="text-gray-200">
                                {memberLabel(member)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="flex items-start gap-2 rounded border border-gray-700 p-3">
                    <Checkbox
                      id="is-primary"
                      checked={formData.isPrimary}
                      onCheckedChange={(checked) => setFormData({ ...formData, isPrimary: checked === true })}
                      data-testid="checkbox-is-primary"
                    />
                    <div>
                      <Label htmlFor="is-primary" className="text-gray-300">Primary mailbox</Label>
                      <p className="text-xs text-gray-500">
                        The primary mailbox is the team inbox used for project email intake and default sending.
                        Only one account can be primary; choosing this replaces the current one after confirmation.
                      </p>
                    </div>
                  </div>

                  {!editingAccount && (
                    <div className="flex justify-center">
                      <Button
                        type="button"
                        onClick={populateHostEuropeDefaults}
                        variant="outline"
                        className="border-blue-500 text-blue-400 hover:bg-blue-600 hover:text-white"
                      >
                        Use HostEurope server settings
                      </Button>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowAddForm(false);
                        setEditingAccount(null);
                        resetForm();
                      }}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={createAccountMutation.isPending || updateAccountMutation.isPending}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {editingAccount ? 'Update Account' : 'Create Account'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
      <AlertDialog open={primaryPrompt != null} onOpenChange={(isOpen) => { if (!isOpen) setPrimaryPrompt(null); }}>
        <AlertDialogContent className="bg-gray-900 border-gray-700 text-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the primary mailbox?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {primaryPrompt?.currentPrimary.email} ({primaryPrompt?.currentPrimary.name}) is currently the primary mailbox
              for project email intake and default sending. Making this account primary demotes it. Continue only if that is intended.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-600 text-gray-300" data-testid="button-cancel-replace-primary">Keep current primary</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPrimaryReplacement} className="bg-red-600 hover:bg-red-700 text-white" data-testid="button-confirm-replace-primary">
              Replace primary
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}